import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './icon/Icon';
import Input from './bootstrap/forms/Input';
import Modal, { ModalBody, ModalHeader } from './bootstrap/Modal';
import SearchService from '../services/SearchService';

const RECENT_KEY = 'ai4_recent_searches';
const MAX_RECENT = 5;

const getRecent = () => {
    try {
        return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch {
        return [];
    }
};

const saveRecent = (query) => {
    if (!query.trim()) return;
    // Prepend the new query, deduplicate, and keep at most MAX_RECENT entries.
    const prev = getRecent().filter((q) => q !== query.trim());
    localStorage.setItem(RECENT_KEY, JSON.stringify([query.trim(), ...prev].slice(0, MAX_RECENT)));
};

const clearRecent = () => localStorage.removeItem(RECENT_KEY);

const TYPE_ICON = { model: 'Psychology', workspace: 'Dashboard' };
const TYPE_LABEL = { model: 'Models', workspace: 'Workspaces' };

// Convert Atlas Search score to 1-3 star bucket (null score = no badge)
const scoreStars = (score) => {
    if (score === null || score === undefined) return null;
    if (score >= 1.5) return 3;
    if (score >= 0.5) return 2;
    return 1;
};

const Search = () => {
    const navigate = useNavigate();
    const refInput = useRef(null);

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [recent, setRecent] = useState(getRecent);
    const debounceRef = useRef(null);

    // Cmd+K / Ctrl+K opens the palette
    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setOpen((prev) => !prev);
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, []);

    useEffect(() => {
        if (open) {
            setTimeout(() => refInput.current?.focus(), 50);
        } else {
            setQuery('');
            setResults([]);
        }
    }, [open]);

    const runSearch = useCallback((q) => {
        if (q.trim().length < 2) {
            setResults([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        SearchService.globalSearch(q.trim())
            .then((data) => setResults(data?.results || []))
            .catch(() => setResults([]))
            .finally(() => setLoading(false));
    }, []);

    const handleInput = (e) => {
        const q = e.target.value;
        setQuery(q);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => runSearch(q), 200);
    };

    const handleSelect = (path, label) => {
        saveRecent(query || label);
        setRecent(getRecent());
        setOpen(false);
        navigate(path);
    };

    const handleRecentSelect = (q) => {
        setQuery(q);
        runSearch(q);
    };

    const grouped = results.reduce((acc, r) => {
        if (!acc[r.type]) acc[r.type] = [];
        acc[r.type].push(r);
        return acc;
    }, {});

    const showRecent = query.trim().length < 2 && recent.length > 0;
    const showEmpty = query.trim().length >= 2 && !loading && results.length === 0;

    return (
        <>
            <div
                className='d-flex align-items-center cursor-pointer'
                role='button'
                tabIndex={0}
                onClick={() => setOpen(true)}
                onKeyDown={(e) => e.key === 'Enter' && setOpen(true)}
                title='Search (Cmd+K)'>
                <Icon icon='Search' size='2x' color='primary' />
                <span className='d-none d-md-inline ms-1 text-muted small'>K</span>
            </div>

            <Modal
                setIsOpen={setOpen}
                isOpen={open}
                isStaticBackdrop={false}
                isScrollable
                size='lg'>
                <ModalHeader setIsOpen={setOpen}>
                    <div className='d-flex align-items-center w-100 gap-2'>
                        <Icon icon='Search' size='lg' color='primary' />
                        <Input
                            ref={refInput}
                            type='search'
                            className='border-0 shadow-none bg-transparent flex-grow-1'
                            placeholder='Search models, workspaces (type 2+ chars)'
                            value={query}
                            onChange={handleInput}
                            autoComplete='off'
                        />
                        {loading && (
                            <span className='spinner-border spinner-border-sm text-primary' role='status' aria-label='Searching' />
                        )}
                    </div>
                </ModalHeader>

                <ModalBody>
                    {showEmpty && (
                        <p className='text-muted text-center py-3 mb-0'>
                            No results for &ldquo;{query}&rdquo;
                        </p>
                    )}

                    {showRecent && (
                        <div>
                            <div className='d-flex justify-content-between align-items-center mb-2'>
                                <small className='text-uppercase text-muted fw-semibold'>Recent</small>
                                <button
                                    className='btn btn-sm btn-link text-muted p-0'
                                    onClick={() => { clearRecent(); setRecent([]); }}>
                                    Clear
                                </button>
                            </div>
                            {recent.map((q) => (
                                <button
                                    key={q}
                                    className='d-flex align-items-center gap-2 w-100 px-2 py-1 mb-1 btn btn-light text-start'
                                    onClick={() => handleRecentSelect(q)}>
                                    <Icon icon='History' size='sm' className='text-muted' />
                                    <span>{q}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {Object.keys(grouped).map((type) => (
                        <div key={type} className='mb-3'>
                            <small className='text-uppercase text-muted fw-semibold d-block mb-1'>
                                {TYPE_LABEL[type] || type}
                            </small>
                            <table className='table table-hover table-modern mb-0'>
                                <tbody>
                                    {grouped[type].map((item) => {
                                        const stars = scoreStars(item.score);
                                        return (
                                            <tr
                                                key={item._id}
                                                className='cursor-pointer'
                                                onClick={() => handleSelect(item.path, item.title)}>
                                                <td>
                                                    <Icon
                                                        icon={TYPE_ICON[type] || 'Search'}
                                                        size='sm'
                                                        className='me-2 text-primary'
                                                    />
                                                    <span className='fw-semibold'>{item.title}</span>
                                                    {item.description && (
                                                        <span className='text-muted small ms-2'>
                                                            {item.description.slice(0, 60)}{item.description.length > 60 ? '…' : ''}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className='text-end' style={{ whiteSpace: 'nowrap' }}>
                                                    {stars !== null && (
                                                        <span
                                                            className='text-warning me-2'
                                                            title={`Relevance: ${stars}/3`}
                                                            aria-label={`Relevance ${stars} out of 3 stars`}>
                                                            {'★'.repeat(stars)}{'☆'.repeat(3 - stars)}
                                                        </span>
                                                    )}
                                                    {item.status && (
                                                        <span className='badge bg-secondary'>{item.status}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ))}

                    {!showRecent && !showEmpty && results.length === 0 && query.trim().length < 2 && (
                        <p className='text-muted text-center py-3 mb-0 small'>
                            Type at least 2 characters to search models and workspaces
                        </p>
                    )}

                    <div className='text-muted small text-end mt-2'>
                        <kbd>Enter</kbd> open &nbsp;.&nbsp; <kbd>Esc</kbd> close
                    </div>
                </ModalBody>
            </Modal>
        </>
    );
};

export default Search;
